// Phase G G5 — room reads for the live surfaces (public SELECT via rooms_select_public).

import { queryOptions } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';

export interface LiveRoomDetail {
  id: string;
  slug: string;
  title: string;
  status: string;
  startedAt: string | null;
  livekitRoomName: string;
  membersOnly: boolean;
  queueEnabled: boolean;
  hostId: string;
  tickerLines: string[];
  logoUrl: string | null;
  host: { displayName: string | null; username: string | null; avatarUrl: string | null } | null;
}

function normalizeHost(h: unknown): LiveRoomDetail['host'] {
  const row = Array.isArray(h) ? (h[0] ?? null) : h;
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  return {
    displayName: (r.display_name as string | null) ?? null,
    username: (r.username as string | null) ?? null,
    avatarUrl: (r.avatar_url as string | null) ?? null,
  };
}

export function hostDisplayName(host: LiveRoomDetail['host']): string {
  return host?.displayName ?? host?.username ?? 'ArcheTelos';
}

const ROOM_SELECT =
  'id, slug, title, status, started_at, livekit_room_name, members_only, queue_enabled, host_id, ticker_lines, logo_url, host:profiles!rooms_host_id_fkey(display_name, username, avatar_url)';

interface RawRoom {
  id: string;
  slug: string;
  title: string;
  status: string;
  started_at: string | null;
  livekit_room_name: string;
  members_only: boolean;
  queue_enabled: boolean;
  host_id: string;
  ticker_lines: string[] | null;
  logo_url: string | null;
  host: unknown;
}

function mapDetail(data: RawRoom): LiveRoomDetail {
  return {
    id: data.id,
    slug: data.slug,
    title: data.title,
    status: data.status,
    startedAt: data.started_at,
    livekitRoomName: data.livekit_room_name,
    membersOnly: data.members_only,
    queueEnabled: data.queue_enabled,
    hostId: data.host_id,
    tickerLines: data.ticker_lines ?? [],
    logoUrl: data.logo_url,
    host: normalizeHost(data.host),
  };
}

export const roomQueries = {
  /** Resolve a room for the viewer page. Polls so a mid-watch end is noticed. */
  bySlug: (slug: string) =>
    queryOptions({
      queryKey: ['live-room', slug] as const,
      refetchInterval: 5000,
      queryFn: async (): Promise<LiveRoomDetail | null> => {
        const { data, error } = await supabase
          .from('rooms')
          .select(ROOM_SELECT)
          .eq('slug', slug)
          .maybeSingle();
        if (error) throw error;
        return data ? mapDetail(data) : null;
      },
    }),

  /**
   * The host's most recent still-open room (scheduled or live). Drives the host
   * panel + makes a reload mid-show resume instead of stranding the host. Polled
   * so the panel notices the scheduled→live flip without Realtime.
   */
  mineActive: (hostId: string) =>
    queryOptions({
      queryKey: ['live-room-mine', hostId] as const,
      refetchInterval: 4000,
      queryFn: async (): Promise<LiveRoomDetail | null> => {
        const { data, error } = await supabase
          .from('rooms')
          .select(ROOM_SELECT)
          .eq('host_id', hostId)
          .in('status', ['scheduled', 'live'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error) throw error;
        return data ? mapDetail(data) : null;
      },
    }),

  /** The host's recently ended broadcasts (most recent first) for the Studio
   *  dashboard "Recent" list; each links to its watch/VOD page. */
  recentEnded: (hostId: string, max = 5) =>
    queryOptions({
      queryKey: ['live-room-recent', hostId, max] as const,
      queryFn: async (): Promise<LiveRoomDetail[]> => {
        const { data, error } = await supabase
          .from('rooms')
          .select(ROOM_SELECT)
          .eq('host_id', hostId)
          .eq('status', 'ended')
          .order('ended_at', { ascending: false, nullsFirst: false })
          .limit(max);
        if (error) throw error;
        return (data ?? []).map(mapDetail);
      },
    }),
};
